import type { FormField } from '@/types/api/form'
import { FormFieldType } from '@/types/enums'

const LAYOUT_TYPES: readonly string[] = [
  FormFieldType.SECTION_HEADING,
  FormFieldType.STATIC_TEXT,
  FormFieldType.DIVIDER,
  FormFieldType.LINE_BREAK,
  FormFieldType.PAGE_BREAK,
]

/** Mirrors the backend limits. */
export const HELP_TEXT_MAX = 300
export const STATIC_TEXT_MAX = 5000
export const HEADING_MAX = 200
export const RATING_MIN_STARS = 3
export const RATING_MAX_STARS = 10
export const RATING_DEFAULT_STARS = 5

export type FieldWidth = 'full' | 'half'
export type TextAlign = 'left' | 'center' | 'right'

/** Layout elements (headings, text, dividers, breaks) shape the form and hold no value. */
export function isLayoutField(field: { type?: string }): boolean {
  return LAYOUT_TYPES.includes(field.type ?? '')
}

type Options = Pick<FormField, 'options'>

function opts(field: Options): Record<string, unknown> {
  return (field.options ?? {}) as Record<string, unknown>
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function helpText(field: Options): string {
  return str(opts(field).helpText)
}

/** The width the designer picked, or null to let the layout decide. */
export function fieldWidth(field: Options): FieldWidth | null {
  const width = opts(field).width
  return width === 'full' || width === 'half' ? width : null
}

export function ratingStars(field: Options): number {
  const max = Number(opts(field).max)
  return Number.isInteger(max) && max >= RATING_MIN_STARS && max <= RATING_MAX_STARS ? max : RATING_DEFAULT_STARS
}

export interface HeadingOptions { text: string; subtitle: string; size: 'h2' | 'h3' }
export function headingOptions(field: Options): HeadingOptions {
  const o = opts(field)
  return { text: str(o.text), subtitle: str(o.subtitle), size: o.size === 'h3' ? 'h3' : 'h2' }
}

export interface StaticTextOptions { text: string; align: TextAlign }
export function staticTextOptions(field: Options): StaticTextOptions {
  const o = opts(field)
  return { text: str(o.text), align: o.align === 'center' || o.align === 'right' ? o.align : 'left' }
}

export function dividerStyle(field: Options): 'solid' | 'dashed' {
  return opts(field).style === 'dashed' ? 'dashed' : 'solid'
}

export function pageBreakTitle(field: Options): string {
  return str(opts(field).title)
}
